import type { ReactNode } from 'react'

type Classification = Record<string, unknown>
type ClassificationTrace = {
  dimensionCode: string
  condition: string
  result: string[]
  recordedFacts: { label: string; value: unknown; source?: string }[]
}

const operatingQuestions = [
  { dimension: 'ai_activity', question: 'What does it do?' },
  { dimension: 'user_group', question: 'Who uses it?' },
  { dimension: 'audience_scope', question: 'Who sees the output?' },
  { dimension: 'decision_authority', question: 'Does it make decisions?' },
  { dimension: 'human_oversight', question: 'Is a person involved?' },
  { dimension: 'operating_pattern', question: 'How does it operate?' },
  { dimension: 'technology_family', question: 'Technology' },
] as const

const plainLanguageValues: Record<string, string> = {
  information_content_only: 'No — it provides information or creates content only',
  recommend_action_decision: 'It recommends — a person remains responsible for the decision',
  action_after_person_approves: 'It acts only after a person approves',
  bounded_actions_without_prior_approval: 'Yes — it can take defined actions without approval each time',
  makes_materially_determines_decisions: 'Yes — it makes or materially determines decisions',
  controls_physical_equipment: 'Yes — it can control physical equipment',
  review_every_output_action: 'Yes — a person reviews every output or action',
  review_selected_exceptional: 'Yes — people review selected or exceptional cases',
  review_after_output_action: 'Yes — review happens after the output or action',
  no_routine_review: 'No routine human review',
  internal: 'Internal users',
  external: 'People outside the organisation',
  both: 'Internal and external users',
}

function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function titleCase(value: string) { return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase()) }
function displayValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join(', ') || 'Not recorded'
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  if (typeof value === 'string') return value ? titleCase(value) : 'Not recorded'
  if (typeof value === 'number') return String(value)
  return 'Not recorded'
}

function classificationValue(item: Classification) {
  const code = text(item.valueCode)
  return plainLanguageValues[code] ?? text(item.label) ?? titleCase(code)
}

function sourceFor(dimension: string, item?: Classification) {
  if (dimension === 'technology_family') return text(item?.confirmationStatus) === 'needs_confirmation' ? 'Specialist confirmation' : 'Product or technical information'
  if (['user_group', 'human_oversight'].includes(dimension)) return 'Recorded contributor information'
  if (['ai_activity', 'operating_pattern', 'decision_authority', 'audience_scope'].includes(dimension)) return 'Saved deterministic mapping'
  return 'Recorded governance information'
}

export function AiUseOperatingModel({
  classifications,
  traces,
  technologyAction,
}: {
  classifications: Classification[]
  traces: ClassificationTrace[]
  technologyAction?: ReactNode
}) {
  const grouped = classifications.reduce<Record<string, Classification[]>>((result, item) => {
    const dimension = text(item.dimensionCode)
    if (dimension) (result[dimension] ??= []).push(item)
    return result
  }, {})
  const visible = operatingQuestions.filter(({ dimension }) => (grouped[dimension]?.length ?? 0) > 0)

  return <section className="panel register-detail-section ai-use-operating-model" id="derived-classification" aria-labelledby="ai-use-operating-heading">
    <div className="section-heading"><div><p className="eyebrow">Operating model</p><h2 id="ai-use-operating-heading">How this AI use operates</h2><p>A plain-English view of what the AI does, who uses it and how people remain involved.</p></div></div>
    {visible.length === 0 ? <div className="inline-alert neutral">The operating model still needs confirmation. Swell keeps the AI use visible rather than asking someone to guess.</div> : <div className="ai-use-operating-grid">
      {visible.map(({ dimension, question }) => {
        const values = grouped[dimension] ?? []
        const trace = traces.find((item) => item.dimensionCode === dimension)
        const facts = trace?.recordedFacts ?? []
        return <article key={dimension} className="ai-use-operating-item">
          <span>{question}</span>
          <strong>{values.map(classificationValue).join(', ')}</strong>
          <details>
            <summary>Why?</summary>
            <div className="ai-use-trace-detail">
              <h3>How Swell determined this</h3>
              {facts.length ? <dl>{facts.map((fact, index) => <div key={`${dimension}-${text(fact.label) || index}`}><dt>{text(fact.label) || 'Recorded fact'}</dt><dd>{displayValue(fact.value)}</dd></div>)}</dl> : <p>{sourceFor(dimension, values[0])}.</p>}
              {trace?.condition ? <p><strong>Saved rule:</strong> {trace.condition}</p> : null}
              <p><strong>Result:</strong> {trace?.result?.join(', ') || values.map(classificationValue).join(', ')}</p>
            </div>
          </details>
          {dimension === 'technology_family' ? technologyAction : null}
        </article>
      })}
    </div>}
  </section>
}

export { operatingQuestions, plainLanguageValues }
